import React, { ComponentProps, useEffect, useState } from 'react';
import { Button } from '@fiverr-private/button';
import { CheckIcon } from '@fiverr-private/visuals';
import { translate } from '@fiverr-private/i18n-react';
import { raiseToast } from '@fiverr-private/toast';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { RecommendedSeller } from '../../../types/seller';
import { useSendMessage } from '../../hooks/useSendMessage';
import { useSignUp } from '../../hooks/useSignUp';
import { useLandingPage } from '../../context/LandingPageContext';
import { FREELANCER_PAGE_PATHFINDER_KEY } from '../../consts';
import { reportClickedOnExpertCardEvent, reportClickedOnSendBriefEvent } from '../../utils/mixpanel';

export const useSellersWithCTA = ({
    onMessageSent,
    onSignUpSuccess,
}: {
    onMessageSent: (username: string) => void;
    onSignUpSuccess: () => void;
}) => {
    const [sellersWithCTA, setSellersWithCTA] = useState<any[]>([]);
    const { sendMessage } = useSendMessage();
    const { sellers } = useLandingPage();
    const { userId } = getContext();
    const { signUp } = useSignUp();
    const [sellersWithMessageSent, setSellersWithMessageSent] = useState<number[]>([]);
    const [sellersInLoadingMode, setSellersInLoadingMode] = useState<number[]>([]);

    useEffect(() => {
        if (sellers.length > 0) {
            const updatedSellers = sellers.map((seller: RecommendedSeller) => {
                const { id, username, displayName } = seller.seller;
                const isMessageSent = sellersWithMessageSent.includes(id);
                const isLoading = sellersInLoadingMode.includes(id);

                const onClick = () => {
                    reportClickedOnExpertCardEvent(id, displayName, 'Vibe coding brief page');
                    window.open(pathfinder(FREELANCER_PAGE_PATHFINDER_KEY, { id: username }), '_blank');
                };

                const sendMessageButton = {
                    onClick: async () => {
                        reportClickedOnSendBriefEvent(id);
                        setSellersInLoadingMode([...sellersInLoadingMode, id]);
                        try {
                            if (!userId) {
                                signUp(onSignUpSuccess);
                            } else {
                                await sendMessage({
                                    sellerUsername: username,
                                });
                                setSellersWithMessageSent([...sellersWithMessageSent, id]);
                                onMessageSent(username);
                            }
                        } catch (error) {
                            raiseToast({
                                type: 'error',
                                location: 'top',
                                message: translate('vibe_coding_perseus.seller_message.something_went_wrong'),
                            });
                        } finally {
                            setSellersInLoadingMode(sellersInLoadingMode.filter((sellerId) => sellerId !== id));
                        }
                    },
                    isCta: true,
                    elementName: 'sendBriefButton',
                    label: translate(
                        `vibe_coding_perseus.listings.${isMessageSent ? 'brief_sent_button' : 'send_brief_button'}`
                    ),
                    icon: isMessageSent ? <CheckIcon color="grey_800" /> : null,
                    variant: 'filled' as ComponentProps<typeof Button>['variant'],
                    disabled: isMessageSent,
                    loading: isLoading,
                };
                return {
                    ...seller,
                    headerCustomButtons: [sendMessageButton],
                    footerCustomButtons: [sendMessageButton],
                    onClick,
                    onSellerDetailsClick: onClick,
                };
            });

            setSellersWithCTA(updatedSellers);
        }
    }, [
        sellers,
        sendMessage,
        sellersInLoadingMode,
        sellersWithMessageSent,
        onMessageSent,
        onSignUpSuccess,
        userId,
        signUp,
    ]);

    return {
        sellersWithCTA,
    };
};
